import * as Vonq from "/../../vendor/jobtoolz/vonq/public/js/share/Vonq.js";
import * as booster from "../../services/BoosterService";
import * as CampaignService from "../../services/CampaignService";
import Booster from "../../models/Booster";
import { normalizeJson } from "../../../shared/helpers";
import Form from "../../etc/forms/Form";
import PaidSubscription from "../../models/PaidSubscription";
import _, { result } from "lodash";
import {defineStore} from "pinia";
import {useMainStore} from "../store";
import {useRepo} from "pinia-orm";

function multiPosting(currentCompany) {
    return !!useRepo(PaidSubscription)
            .where('type', 'multi-posting')
            .where('company_id', currentCompany.id)
            .where('active', true)
            .first() && currentCompany.multiposting_status === 'active';
};

function newState(retain = {}) {
    return {
        pagination: Vonq.getPagination(),
        fetchingProducts: false,
        fetchingNextProducts: false,
        creatingCampaign: false,
        defaultStep: 1,
        selectedItems: [],
        products: [],
        contracts: [],
        contractGroupsHolder: [],
        contractCache: [],
        boosters: [],
        channels: [],
        campaigns: [],
        campaign: {},
        form: new Form([
            { name: "title", value: "Draft" },
            { name: "jobFunctionId", value: null },
            { name: "jobFunctionName", value: null },
            { name: "educationLevelId", value: null },
            { name: "educationLevelName", value: null },
            { name: "seniorityId", value: null },
            { name: "seniorityName", value: null },
            { name: "industryId", value: null },
            { name: "industryName", value: null },
            { name: "yearsOfExperience", value: 0 },
            { name: "employmentType", value: null },
            { name: "weeklyWorkingHoursFrom", value: 0 },
            { name: "weeklyWorkingHoursTo", value: 0 },
            { name: "salaryIndicationPeriod", value: null },
            { name: "salaryIndicationFrom", value: null },
            { name: "salaryIndicationTo", value: null },
            { name: "salaryIndicationCurrency", value: null },
            { name: "jobId", value: null },
            { name: "startDate", value: null },
            { name: "region", value: null },
            { name: "publicationLanguage", value: null },
            { name: "description", value: null },
            { name: "profileAndOffer", value: null },
            { name: "editable", value: true },
            { name: "timeToProcess", value: 0 },
            { name: "timeToSetup", value: 0 },
        ]),
        postingRequirementForms: {},
        postingRequirementFields: {},
        filters: {
            search: "",
            sortBy: "list_price.asc",
            jobTitleId: "",
            jobFunctionId: "",
            industryId: "",
            locationId: "",
            channelTypeId: "",
        },
        lookups: {
            jobFunctions: [],
            industries: [],
            productTypes: [],
            jobTitles: [],
            locations: [],
            educationLevels: [],
            seniority: [],
        },
        productJobs: [],
        productJobData: [],
        selectedComponent: retain.selectedComponent || "SocialBoosts",
        selectedTab: retain.selectedTab || "SocialBoosts",
        purchasing: false,
        validatingPostingRequirements: false,
        requiresComfirmation: false,
        contractAuthState: {},
        hasLegacyCampaigns: false,
    };
}

export const useMultiPostingStore = defineStore('multiPosting', {
    state: () => ({
        tab: "allProducts",
        mainStore: useMainStore(),
        ...newState(),
    }),
    getters: {
        getLookupEntryByKey: (state) => (key, value) => {
            const lookup = state.lookups[key];
            if (lookup) {
                return lookup.find((entry) => entry.value === value);
            }
            return null;
        },
        contractGroups(state) {
            let contractGroups = state.contractGroupsHolder;

            if (contractGroups && contractGroups.length > 0) {
                const groupsWithIndex = contractGroups.filter(group => group.position !== undefined);
                const groupsWithoutIndex = contractGroups.filter(group => group.position === undefined);
                const sortedGroups = groupsWithIndex.sort((a, b) => a.position - b.position);
                contractGroups = [...sortedGroups, ...groupsWithoutIndex.sort((a, b) => a.name.localeCompare(b.name))];

                const defaultGroup = contractGroups.find(group => group.name === "Default");
                if (defaultGroup) {
                    contractGroups.splice(contractGroups.indexOf(defaultGroup), 1);
                    contractGroups.unshift(defaultGroup);
                }

                contractGroups = contractGroups.filter(group => !group.isUUID);
            }

            return contractGroups;
        },
    },
    actions: {
        async addSelectedItems(payload) {
            this.SET_SELECTED_ITEMS({ payload, keepResults: true });
            payload.forEach((item) => {
                if (item.type === "contract" && !this.postingRequirementForms[item.contract_id]) {
                    // fetch contract posting requirements
                    Vonq.api({
                        action: "getContractsByIds",
                        userId: this.mainStore.currentCompany.id,
                        contractIds: [item.contract_id],
                    }).then((data) => {
                        this.UPDATE_CONTRACT(data[0]);
                        this.SET_CHANNEL_FORM({ contract: data[0], data: item.posting_requirement_data });
                    });
                }
            });
        },
        async fetchProducts({ pagination = null, next = false }) {
            try {
                this.CONVERT_NULL_TO_STRING_FILTERS();

                let userId = this.mainStore.currentCompany.id;
                const {
                    sortBy = "",
                    search = "",
                    jobFunctionId = "",
                    jobTitleId = "",
                    locationId = "",
                    channelTypeId = "",
                    industryId = "",
                } = this.filters;

                if (next) {
                    this.fetchingNextProducts = true;
                    return await this.pagination
                        .next()
                        .then((data) => {
                            this.SET_PRODUCTS({ payload: data, keepResults: true });
                        })
                        .then(() => (this.fetchingNextProducts = false));
                } else {
                    this.fetchingProducts = true;
                    return await Vonq.api({
                        action: "searchProducts",
                        size: 50,
                        input: search ?? "",
                        pagination,
                        sortBy,
                        userId,
                        industryId,
                        jobFunctionId,
                        jobTitleId,
                        locationId,
                        channelTypeId,
                    })
                        .then((data) => this.SET_PRODUCTS({ payload: data }))
                        .then(() => (this.fetchingProducts = false));
                }
            } catch (error) {
                throw error;
            }
        },
        async filterContracts() {
            let result = [];
            const search = this.filters.search.toLowerCase();
            if (search.length > 0) {
                for (let i = 0; i < this.contractCache.length; i++) {
                    const contract = this.contractCache[i];
                    if (contract.name.toLowerCase().includes(search)) {
                        result.push(contract);
                    }
                }
                if (result.length > 0) {
                    this.SET_CONTRACTS(result);
                    return;
                }
            }
            // reset to full list of contract
            this.SET_CONTRACTS(this.contractCache);
        },
        async fetchContracts() {
            if (!multiPosting(this.mainStore.currentCompany)) return;
            try {
                const userId = this.mainStore.currentCompany.id;
                return await Vonq.api({
                    action: "getContracts",
                    includeExtra: false,
                    userId,
                }).then((data) => {
                    this.SET_CONTRACTS(data);
                    this.SET_CONTRACT_CACHE(data);
                });
            } catch (error) {
                console.error(error);
                throw error;
            }
        },
        async deleteContract(payload) {
            if (!multiPosting(this.mainStore.currentCompany)) return;
            try {
                const userId = this.mainStore.currentCompany.id;
                return await Vonq.api({
                    action: "deleteContract",
                    userId,
                    contractId: payload,
                });
            } catch (error) {
                console.error(error);
                throw error;
            }
        },
        async filterBoosters(locale) {
            const search = this.filters.search.toLowerCase();
            const boosters = useRepo(Booster)
                .where((booster) => {
                    return (
                        booster.active &&
                        booster.title !== null &&
                        booster.title[locale] &&
                        booster.title[locale].toLowerCase().includes(search)
                    );
                })
                .orderBy("sequence")
                .get();
            this.SET_BOOSTERS(boosters);
        },
        async fetchBoosters() {
            try {
                return await booster.index().then(({ data }) => {
                    useRepo(Booster).save(normalizeJson(data));
                    this.SET_BOOSTERS(useRepo(Booster).where("active", true).get());
                });
            } catch (error) {
                console.error(error);
                throw error;
            }
        },
        async fetchTaxonomyJobFunctions() {
            try {
                return await Vonq.api({
                    action: "getTaxonomyJobFunctions",
                    userId: this.mainStore.currentCompany.id,
                }).then((result) => this.SET_TAXONOMY_JOB_FUNCTIONS(result));
            } catch (error) {
                console.error(error);
                throw error;
            }
        },
        async fetchTaxonomyIndustries() {
            try {
                return await Vonq.api({
                    action: "getTaxonomyIndustries",
                    userId: this.mainStore.currentCompany.id,
                }).then((result) => this.SET_TAXONOMY_INDUSTRIES(result));
            } catch (error) {
                console.error(error);
                throw error;
            }
        },
        async fetchProductTypes() {
            try {
                return await Vonq.api({
                    action: "getProductTypes",
                    userId: this.mainStore.currentCompany.id,
                }).then((result) => this.SET_PRODUCT_TYPES(result));
            } catch (error) {
                console.error(error);
                throw error;
            }
        },
        async fetchTaxonomyJobTitles(payload) {
            try {
                return await Vonq.api({
                    action: "getTaxonomyJobtitles",
                    userId: this.mainStore.currentCompany.id,
                    text: payload,
                }).then((result) => {
                    this.SET_JOB_TITLES(result);
                });
            } catch (error) {
                console.error(error);
                throw error;
            }
        },
        async fetchTaxonomyLocations(payload) {
            try {
                return await Vonq.api({
                    action: "getTaxonomyLocations",
                    userId: this.mainStore.currentCompany.id,
                    text: payload,
                }).then((result) => {
                    this.SET_LOCATIONS(result);
                });
            } catch (error) {
                console.error(error);
                throw error;
            }
        },
        async fetchTaxonomyEducationLevels() {
            try {
                return await Vonq.api({
                    action: "getTaxonomyEducationLevels",
                    userId: this.mainStore.currentCompany.id,
                }).then((result) => this.SET_EDUCATION_LEVELS({ payload: result }));
            } catch (error) {
                console.error(error);
                throw error;
            }
        },
        async fetchTaxonomySeniority() {
            try {
                return await Vonq.api({
                    action: "getTaxonomySeniority",
                    userId: this.mainStore.currentCompany.id,
                }).then((result) => this.SET_SENIORITY({ payload: result }));
            } catch (error) {
                console.error(error);
                throw error;
            }
        },
        async fetchLookups() {
            try {
                await Promise.all([
                    this.fetchTaxonomyJobFunctions(),
                    this.fetchTaxonomyIndustries(),
                    this.fetchProductTypes(),
                    this.fetchTaxonomyEducationLevels(),
                    this.fetchTaxonomySeniority(),
                ]);
            } catch (error) {
                console.error(error);
                throw error;
            }
        },
        async fetchContractOptions({ contractId, postingRequirementName, query, shouldSlice }) {
            if (!multiPosting(this.mainStore.currentCompany)) return;
            try {
                return await Vonq.api({
                    action: "contractAutocompleteValues",
                    userId: this.mainStore.currentCompany.id,
                    contractId,
                    params: {
                        postingRequirementName,
                        query,
                    },
                }).then((result) =>
                    this.SET_AUTOCOMPLETE_VALUES({
                        contractId,
                        postingRequirementName,
                        results: result,
                        shouldSlice: shouldSlice,
                    })
                );
            } catch (error) {
                console.error(error);
                throw error;
            }
        },
        async fetchJobBoardData(payload) {
            try {
                return await CampaignService.fetchJobBoardData(payload).then((result) =>
                    this.SET_PRODUCT_JOB_DATA(result)
                );
            } catch (error) {
                console.error(error);
                throw error;
            }
        },
        async fetchProductJobs(payload) {
            try {
                return await CampaignService.fetchProductJobs(payload).then((result) =>
                    this.SET_PRODUCT_JOBS(result)
                );
            } catch (error) {
                console.error(error);
                throw error;
            }
        },
        async fetchContractGroups() {
            if (!multiPosting(this.mainStore.currentCompany)) return;
            try {
                return await Vonq.api({
                    action: "getContractGroups",
                    userId: this.mainStore.currentCompany.id
                }).then((result) => {
                    this.SET_CONTRACT_GROUPS(result);
                    return result;
                }
                );
            } catch (error) {
                console.error(error);
                throw error;
            }
        },
        async purchaseCampaign(payload) {
                const userId = this.mainStore.currentCompany.id;
                const campaignId = payload;

            if (!campaignId) return console.error("No campaign ID provided");

            return await Vonq.api({
                action: "purchaseCampaign",
                userId,
                campaignId,
            })
                .then((results) => {
                    this.setCreatingCampaign(false);
                    return results
                })
                .finally(() => this.setPurchasing(false));
        },
        async savePostingRequirementsData({ campaignId, contractId, data }) {
            try {
                const userId = this.mainStore.currentCompany.id;
                return await Vonq.api({
                    action: "savePostingRequirementsData",
                    userId,
                    campaignId,
                    contractId,
                    data,
                });
            } catch (error) {
                console.error(error);
                throw error;
            }
        },
        async fetchCampaigns(filter) {
            try {
                const userId = this.mainStore.currentCompany.id;
                return await Vonq.api({
                    action: "listCampaigns",
                    userId,
                    filter
                }).then((results) => {
                    this.SET_CAMPAIGNS(results);
                });
            } catch (error) {
                console.error(error);
                throw error;
            }
        },
        loadCampaign(campaign) {
            this.form.jobId = parseInt(campaign.jobId);
            this.form.seniorityId = parseInt(campaign.seniorityId);
            this.form.industryId = parseInt(campaign.industryId);
            this.form.jobFunctionId = parseInt(campaign.jobFunctionId);
            this.form.educationLevelId = parseInt(campaign.educationLevelId);
            this.form.title = campaign.name;
            this.form.educationLevelName = campaign.educationLevelName;
            this.form.jobFunctionName = campaign.jobFunctionName;
            this.form.seniorityName = campaign.seniorityName;
            this.form.industryName = campaign.industryName;
            this.form.region = campaign.region;
            this.form.publicationLanguage = campaign.publicationLanguage;
            this.form.description = campaign.description;
            this.form.startDate = campaign.start_date;
            this.form.editable = campaign.editable;
            this.form.time_to_process = campaign.time_to_process;
            this.form.time_to_setup = campaign.time_to_setup;
            this.form.yearsOfExperience = campaign.yearsOfExperience;
            this.form.employmentType = campaign.employmentType;
            this.form.weeklyWorkingHoursFrom = campaign.weeklyWorkingHoursFrom;
            this.form.weeklyWorkingHoursTo = campaign.weeklyWorkingHoursTo;
            this.form.salaryIndicationPeriod = campaign.salaryIndicationPeriod;
            this.form.salaryIndicationFrom = campaign.salaryIndicationFrom;
            this.form.salaryIndicationTo = campaign.salaryIndicationTo;
            this.form.salaryIndicationCurrency = campaign.salaryIndicationCurrency;
            this.campaign = campaign;

            this.clearSelectedItems();
            this.addSelectedItems(campaign.channels);
        },
        async cancelCampaign(campaignId) {
            try {
                const userId = this.mainStore.currentCompany.id;
                return await Vonq.api({
                    action: "cancelCampaign",
                    userId,
                    campaignId,
                }).then((result) => this.UPDATE_IN_CAMPAIGNS(result));
            } catch (error) {
                console.error(error);
                throw error;
            }
        },
        async cancelCampaignProduct({ campaignId, campaignItemId }) {
            try {
                const userId = this.mainStore.currentCompany.id;
                return await Vonq.api({
                    action: "cancelCampaignProduct",
                    userId,
                    campaignId,
                    campaignItemId,
                }).then((result) => this.UPDATE_IN_CAMPAIGNS(result));
            } catch (error) {
                console.error(error);
                throw error;
            }
        },
        async deleteDraft(campaignId) {
            try {
                return await CampaignService.deleteDraft(campaignId)
                    .then((result) => {
                        this.REMOVE_CAMPAIGN(result.campaignId);
                        return result;
                    });
            } catch (error) {
                console.error(error);
                throw error;
            }
        },
        async duplicateCampaign(campaignId) {
            try {
                const userId = this.mainStore.currentCompany.id;
                return await Vonq.api({
                    action: "duplicateCampaign",
                    userId,
                    campaignId,
                });
            } catch (error) {
                console.error(error);
                throw error;
            }
        },
        async editContractGroup({ groupId, groupName }) {
            try {
                return await Vonq.api({
                    action: "editContractGroup",
                    userId: this.mainStore.currentCompany.id,
                    groupId,
                    groupName,
                }).then((result) => {
                    if (result && result.success) {
                        this.UPDATE_CONTRACT_GROUP_NAME(result);
                    }
                    return result;
                });
            } catch (error) {
                console.error(error);
                throw error;
            }
        },
        async moveContractGroups({ groups }) {
            if (!multiPosting(this.mainStore.currentCompany)) return;
            try {
                return await Vonq.api({
                    action: "moveContractGroups",
                    userId: this.mainStore.currentCompany.id,
                    request : { groups }
                }).then((result) => {
                    this.SET_CONTRACT_GROUPS(result);
                    return result;
                });
            } catch (error) {
                console.error(error);
                throw error;
            }
        },
        async deleteContractGroup({ groupId }) {
            if (!multiPosting(this.mainStore.currentCompany)) return;
            try {
                return await Vonq.api({
                    action: "deleteContractGroup",
                    userId: this.mainStore.currentCompany.id,
                    groupId,
                }).then((result) => {
                    this.REMOVE_CONTRACT_GROUP(groupId);
                    return result;
                });
            } catch (error) {
                console.error(error);
                throw error;
            }
        },
        setCreatingCampaign(payload) {
            this.creatingCampaign = payload;
        },
        setDefaultStep(payload) {
            this.defaultStep = payload;
        },
        updateForm(payload) {
            this.form = payload;
        },
        removeSelectedItemById({ itemId, ref = "id" }) {
            for (let i in this.postingRequirementForms) {
                if (i === itemId) delete this.postingRequirementForms[itemId];
            }
            this.selectedItems = this.selectedItems.filter((item) => item[ref] !== itemId);
        },
        clearSelectedItems() {
            this.selectedItems = [];
        },
        editFilter({ label, value }) {
            this.filters[label] = value;
        },
        setPurchasing(payload) {
            this.purchasing = payload;
        },
        setValidatingPostingRequirements(payload) {
            this.validatingPostingRequirements = payload;
        },
        resetFilters(payload = undefined) {
            let filters = {
                search: "",
                sortBy: "list_price.asc",
                jobTitleId: "",
                jobFunctionId: "",
                industryId: "",
                locationId: "",
                channelTypeId: "",
                tab: "allProducts",
            };

            if (!payload) {
                this.filters = filters;
            } else if (Array.isArray(payload)) {
                payload.forEach((x) => (this.filters[x] = filters[x]));
            } else {
                this.filters[payload] = filters[payload];
            }
        },
        resetPagination() {
            this.pagination = Vonq.getPagination();
        },
        setTab(payload) {
            if (!["allProducts", "socialChannels", "contracts"].includes(payload)) {
                this.tab = "allProducts";
                return;
            }
            this.tab = payload;
        },
        clearCreatingCampaign() {
            // keep campaigns
            const retainState = {  selectedComponent: this.selectedComponent, selectedTab: this.selectedTab};
            const campaigns = [...this.campaigns];
            Object.assign(this, newState(retainState));
            this.campaigns = campaigns;
        },
        applyJobData(payload) {
            const job = payload.job;
            if (!job) return;

            this.form.jobId = job.id;
            this.form.region = job.active_location?.country;
        },
        applyLocaleData(payload) {
            const job = payload.job;
            const locale = payload.locale ?? this.form.publicationLanguage;
            if (!job || !locale) return;

            let description = "";

            if (job.description_title[locale]) description += `<h2>${job.description_title[locale]}</h2>`;
            if (job.description[locale]) description += job.description[locale];

            if (job.profile_title[locale]) description += `<h2>${job.profile_title[locale]}</h2>`;
            if (job.profile[locale]) description += job.profile[locale];

            if (job.offer_title[locale]) description += `<h2>${job.offer_title[locale]}</h2>`;
            if (job.offer[locale]) description += job.offer[locale];

            this.form.description = description;
            this.form.title = job.title[locale];
        },
        setSelectedComponent(payload) {
            this.selectedComponent = payload;
        },
        SET_SELECTED_ITEMS({ payload, keepResults = false }) {
            if (keepResults) {
                this.selectedItems = [...this.selectedItems, ...payload];
            } else {
                this.selectedItems = payload;
            }
        },
        SET_PRODUCTS({ payload, keepResults = false }) {
            if (keepResults) {
                this.products = [...this.products, ...payload];
            } else {
                this.products = payload;
            }
        },
        SET_CONTRACTS(payload) {
            this.contracts = payload;
        },
        SET_CONTRACT_CACHE(payload) {
            this.contractCache = payload;
        },
        SET_BOOSTERS(payload) {
            this.boosters = payload;
        },
        UPDATE_CONTRACT(payload) {
            const index =
                this.contracts.length > 0
                    ? this.contracts.findIndex((x) => x.contract_id === payload.contract_id)
                    : 0;
            this.contracts[index] = payload;
        },
        SET_TAXONOMY_JOB_FUNCTIONS(payload) {
            this.lookups.jobFunctions = payload;
        },
        SET_TAXONOMY_INDUSTRIES(payload) {
            this.lookups.industries = payload;
        },
        SET_PRODUCT_TYPES(payload) {
            this.lookups.productTypes = payload;
        },
        SET_JOB_TITLES(payload) {
            this.lookups.jobTitles = payload;
        },
        SET_LOCATIONS(payload) {
            this.lookups.locations = payload;
        },
        SET_EDUCATION_LEVELS({ payload }) {
            this.lookups.educationLevels = payload.map((x) => {
                const value =
                    x.label.find((y) => y.languageCode.includes(this.mainStore.selectedLocale)).value ??
                    x.label.find((y) => y.languageCode.includes("en")).value ??
                    x.label[0].value;

                return {
                    value: x.value,
                    label: value,
                };
            });
        },
        SET_SENIORITY({ payload }) {
            this.lookups.seniority = payload.map((x) => {
                const value =
                    x.label.find((y) => y.languageCode.includes(this.mainStore.selectedLocale)).value ??
                    x.label.find((y) => y.languageCode.includes("en")).value ??
                    x.label[0].value;

                return {
                    value: x.value,
                    label: value,
                };
            });
        },
        CONVERT_NULL_TO_STRING_FILTERS() {
            Object.keys(this.filters).forEach((x) => (this.filters[x] = this.filters[x] ?? ""));
        },
        PUSH_CHANNEL(payload) {
            this.channels.push(payload);
        },
        SET_CHANNEL_FORM({ contract, data }) {
            data = data || {};
            const contractId = contract.contract_id;
            const postingRequirements = contract.postingRequirements;
            let fields = postingRequirements.map((x) => {
                let valueIsArray =
                    x.type === "MULTIPLE" ||
                    x.type === "TEXTEXPAND" ||
                    x.rules?.find((r) => r.rule === "maxitems");
                return { name: x.name, value: valueIsArray ? [] : null };
            });
            let form = new Form(fields);
            postingRequirements
                .filter((x) => x.required)
                .forEach((x) => {
                    form.rules[x.name] = {
                        required: true,
                    };
                });

            let productFormState = {};
            if (this.campaign && this.campaign.channels && this.campaign.channels.length !== 0) {
                const matchingContract = this.campaign.channels.find((contract) => String(contract.contract_id) === String(contractId));
                if (matchingContract) {
                    productFormState = matchingContract.form_state ? JSON.parse(matchingContract.form_state) : {};
                }
            }

            if (data) {
                const defaultData = data;
                postingRequirements.forEach((x) => {
                    if (defaultData[x.name]) {
                        form.data[x.name] = defaultData[x.name];
                    } else {
                        const isRequiredNumber = x.required && x.rules && x.rules.some((r) => r.rule === "int");
                        if (isRequiredNumber) {
                            form.data[x.name] = 0;
                        }
                    }
                    if (productFormState && productFormState._options && productFormState._options[x.name]) {
                        x.options = productFormState._options[x.name];
                    }
                });
            }
            this.postingRequirementForms[contractId] = form;
            this.postingRequirementFields[contractId] = postingRequirements;
        },
        SET_AUTOCOMPLETE_VALUES({ contractId, postingRequirementName, results, shouldSlice }) {
            results = shouldSlice ? results.slice(0, 200) : results;
            const postingRequirements = this.postingRequirementFields[contractId];
            if (!postingRequirements) return;
            postingRequirements.find((y) => y.name === postingRequirementName).options = results;
            this.contracts = _.sortBy(this.contracts, ["contract_id"]);
        },
        SET_PRODUCT_JOB_DATA(payload) {
            this.productJobData = payload;
        },
        SET_PRODUCT_JOBS(payload) {
            this.productJobs = payload;
        },
        SET_CAMPAIGNS(payload) {
            const hasLegacyCampaigns = payload.some((campaign) => campaign.brockmeyer_old_partner);
            if (!this.hasLegacyCampaigns && hasLegacyCampaigns) {
                this.hasLegacyCampaigns = true;
            }
            this.campaigns = payload;
        },
        ADD_OR_UPDATE_CAMPAIGNS(payload) {
            if (this.campaigns.length == 0) {
                this.campaigns = [...payload];
                return;
            }
            payload.forEach((p) => {
                const index = this.campaigns.findIndex((c) => c.id === p.id);
                if (index < 0) {
                    this.campaigns.push(p); // add new
                } else {
                    this.campaigns[index] = p; // update existing
                }
            });
        },
        UPDATE_IN_CAMPAIGNS(payload) {
            const index = this.campaigns.findIndex((x) => x.id === payload.id);
            this.campaigns[index] = payload;
            this.campaigns = [...this.campaigns];
        },
        REMOVE_CAMPAIGN(campaignId) {
            this.campaigns = this.campaigns.filter((x) => x.guid !== campaignId);
        },
        setPostingRequirementFormValue({ contractId, postingRequirementName, value }) {
            let form = this.postingRequirementForms[contractId];
            this.postingRequirementForms[contractId].data[postingRequirementName] = value;
            this.postingRequirementForms = {
                ...this.postingRequirementForms,
                [contractId]: form,
            };
        },
        setPostingRequirementFormErrors({ contractId, errors }) {
            let form = this.postingRequirementForms[contractId];
            this.postingRequirementForms[contractId].setErrors(errors);
            this.postingRequirementForms = {
                ...this.postingRequirementForms,
                [contractId]: form,
            };
        },
        setRequiresConfirmation(requiredConfirmation) {
            this.requiresComfirmation = requiredConfirmation;
        },
        SET_CONTRACT_GROUPS(payload) {
            this.contractGroupsHolder = payload;
        },
        UPDATE_CONTRACT_GROUP_NAME(payload) {
            const index = this.contractGroupsHolder.findIndex((groupId) => groupId.id === payload.id);
            this.contractGroupsHolder[index].name = payload.name;
        },
        REMOVE_CONTRACT_GROUP(groupId) {
            this.contractGroupsHolder = this.contractGroupsHolder.filter((group) => group.id !== groupId);
        },
        setContractAuthState(payload) {
            if (!payload || !payload?.state || !payload?.hash) {
                return
            }
            const decodedState = JSON.parse(atob(payload.state))
            if (decodedState
                && decodedState?.contractId
                && decodedState?.fieldName) {

                this.contractAuthState = {}
                this.contractAuthState = decodedState
                this.contractAuthState['hash'] = payload.hash
                this.contractAuthState['valid'] = true
            }
        },
        clearContractAuthState() {
            this.contractAuthState = {}
        },
        setVacancyId({ jobId }) {
            this.form.jobId = jobId;
        }
    },
});